import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';
import { LearningService } from '../../../../core/services/learning-path.service';
import { PracticeContextService } from '../../../../core/services/practice-context.service';
import { StudentTestService } from '../../../../core/services/student-test.service';
import { StudentService } from '../../../../core/services/student.service';
import {
  PracticeDifficulty,
  PracticeMode,
  PracticePool,
} from '../../../../core/interfaces/practice';
import { Subject as SubjectDto } from '../../../../core/interfaces/subject';

/** Niveau de diversité que le vivier autorise. */
type Diversity = 'none' | 'low' | 'warn' | 'ok';

/**
 * Génération d'un test d'entraînement par l'élève.
 *
 * <p>L'élève choisit sa matière et ses chapitres ; le moteur compose selon sa
 * progression. L'écran ne cache pas la contrainte du vivier : avant de générer,
 * il annonce combien de tests réellement distincts sont possibles. Si un
 * chapitre est trop pauvre en questions, aucun moteur ne peut produire un test
 * différent à chaque fois — le dire vaut mieux que de laisser l'élève constater
 * la répétition.</p>
 *
 * <p>Générer <b>démarre le test</b>. Les questions ne sont pas listées avant :
 * un aperçu transforme le test en document — l'élève le lit, cherche les
 * réponses ailleurs, et le score ne mesure plus rien. La justification de
 * chaque question, qui faisait l'intérêt de cet aperçu, est conservée et
 * affichée pendant la passation via {@link PracticeContextService}.</p>
 *
 * <p>Aucune donnée n'est fabriquée : une erreur réseau donne un état d'erreur
 * avec réessai.</p>
 */
@Component({
  selector: 'app-practice-generator',
  standalone: true,
  imports: [TranslateModule, ErrorStateComponent],
  templateUrl: './practice-generator.component.html',
  styleUrl: './practice-generator.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeGeneratorComponent implements OnInit {
  private readonly learning = inject(LearningService);
  private readonly studentTests = inject(StudentTestService);
  private readonly students = inject(StudentService);
  private readonly practiceContext = inject(PracticeContextService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** Matière demandée par l'appelant (`?subject=`), pré-sélectionnée au chargement. */
  private requestedSubjectId: number | null = null;

  /* ── Portée ────────────────────────────────────────────── */

  readonly subjects = signal<SubjectDto[]>([]);
  readonly subjectId = signal<number | null>(null);
  readonly loadingSubjects = signal(false);
  readonly subjectsErrored = signal(false);

  /** Chapitres cochés. */
  readonly picked = signal<Set<number>>(new Set());

  /* ── Vivier ────────────────────────────────────────────── */

  readonly pool = signal<PracticePool | null>(null);
  readonly loadingPool = signal(false);

  /* ── Réglages ──────────────────────────────────────────── */

  readonly mode = signal<PracticeMode>('learn');
  readonly count = signal(10);
  readonly difficulty = signal<PracticeDifficulty>('auto');

  /* ── Génération ────────────────────────────────────────── */

  readonly generating = signal(false);

  /* ── Dérivés ───────────────────────────────────────────── */

  /** Chapitres du vivier restreints à ceux effectivement cochés. */
  readonly pickedThemes = computed(() => {
    const p = this.picked();
    return (this.pool()?.themes ?? []).filter((t) => p.has(t.themeId));
  });

  /** Questions disponibles sur les chapitres cochés, hors vues récentes. */
  readonly availableForSelection = computed(() =>
    this.pickedThemes().reduce((sum, t) => sum + t.availableCount, 0),
  );

  /** Nombre de tests entièrement distincts que le vivier permet. */
  readonly distinctTests = computed(() => {
    const want = this.count();
    if (want <= 0) return 0;
    return Math.floor(this.availableForSelection() / want);
  });

  readonly diversity = computed<Diversity>(() => {
    if (this.picked().size === 0) return 'none';
    if (this.availableForSelection() < this.count()) return 'low';
    return this.distinctTests() < 3 ? 'warn' : 'ok';
  });

  readonly canGenerate = computed(
    () => this.picked().size > 0 && !this.generating(),
  );

  ngOnInit(): void {
    // « Composer un entraînement » depuis l'espace d'une matière arrive avec
    // celle-ci : la re-demander serait une étape pour rien.
    const requested = Number(this.route.snapshot.queryParamMap.get('subject'));
    this.requestedSubjectId = Number.isFinite(requested) && requested > 0 ? requested : null;
    this.loadSubjects();
  }

  /* ── Matières de l'élève ───────────────────────────────── */

  loadSubjects(): void {
    this.loadingSubjects.set(true);
    this.subjectsErrored.set(false);

    // La progression par matière porte exactement les matières du parcours de
    // l'élève : c'est la bonne source, plutôt que le catalogue global.
    this.students.getMySubjectsProgression().subscribe({
      next: (res) => {
        const subjects = (res?.data ?? []).map((p) => ({
          id: p.subjectId,
          name: p.subjectName,
        })) as SubjectDto[];
        this.subjects.set(subjects);
        this.loadingSubjects.set(false);

        // La matière demandée doit appartenir au parcours de l'élève : un id
        // arbitraire dans l'URL ne doit pas ouvrir un vivier qui n'est pas le sien.
        const requested = this.requestedSubjectId;
        if (requested && subjects.some((s) => s.id === requested)) {
          this.selectSubject(requested);
        }
      },
      error: (_: HttpErrorResponse) => {
        this.subjects.set([]);
        this.subjectsErrored.set(true);
        this.loadingSubjects.set(false);
      },
    });
  }

  selectSubject(id: number): void {
    if (this.subjectId() === id) return;
    this.subjectId.set(id);
    this.picked.set(new Set());
    this.pool.set(null);
    this.loadPool();
  }

  /* ── Vivier ────────────────────────────────────────────── */

  private loadPool(): void {
    const id = this.subjectId();
    if (!id) return;

    this.loadingPool.set(true);
    // On charge le vivier de TOUTE la matière : l'élève coche ensuite, et les
    // compteurs se recalculent en mémoire sans nouvel appel.
    this.studentTests.getPracticePool(id).subscribe({
      next: (res) => {
        this.pool.set(res?.data ?? null);
        // Par défaut, on pré-coche les chapitres les moins maîtrisés : c'est ce
        // que l'élève a le plus intérêt à travailler.
        const themes = res?.data?.themes ?? [];
        const weakest = [...themes]
          .sort((a, b) => (a.masteryPercent ?? 100) - (b.masteryPercent ?? 100))
          .slice(0, 2)
          .map((t) => t.themeId);
        this.picked.set(new Set(weakest));
        this.loadingPool.set(false);
      },
      error: () => {
        this.pool.set(null);
        this.loadingPool.set(false);
      },
    });
  }

  toggleTheme(themeId: number): void {
    this.picked.update((set) => {
      const next = new Set(set);
      if (next.has(themeId)) next.delete(themeId);
      else next.add(themeId);
      return next;
    });
  }

  /* ── Réglages ──────────────────────────────────────────── */

  setMode(mode: PracticeMode): void {
    this.mode.set(mode);
  }

  setDifficulty(value: string): void {
    this.difficulty.set(value as PracticeDifficulty);
  }

  setCount(raw: string): void {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    this.count.set(Math.min(40, Math.max(5, Math.round(n))));
  }

  masteryColor(percent: number | null): string {
    if (percent === null) return 'var(--muted, #6b6f86)';
    if (percent >= 75) return 'var(--cgreen4, #16a34a)';
    if (percent >= 45) return 'var(--cyellow5, #d97706)';
    return 'var(--cred4, #dc2626)';
  }

  /* ── Génération ────────────────────────────────────────── */

  /**
   * Compose le test **et enchaîne sur la passation**. Aucune liste de questions
   * n'est rendue entre les deux : voir la note de classe.
   */
  generate(): void {
    const id = this.subjectId();
    if (!id || !this.canGenerate()) return;

    this.generating.set(true);

    this.studentTests
      .generatePractice({
        subjectId: id,
        themeIds: [...this.picked()],
        questionCount: this.count(),
        mode: this.mode(),
        difficulty: this.difficulty(),
      })
      .subscribe({
        next: (res) => {
          const result = res?.data ?? null;
          this.generating.set(false);
          if (!result?.testId) return;

          // Les raisons de tirage n'existent qu'ici : on les confie au relais
          // avant de naviguer, la session les affichera question par question.
          this.practiceContext.remember(result);
          this.router.navigate(['/student/test/take', result.testId]);
        },
        error: () => {
          // L'intercepteur global a déjà signalé l'échec.
          this.generating.set(false);
        },
      });
  }
}
